import { Fragment, useState } from "react";
import { Link } from "react-router";
import { FOUNDER_SLOTS } from "@handvero/shared";
import { BoardDemo, CountUp, DemoPlayer, LineArtDefs, ProofPaper, Statement } from "../components/Showcase";
import { HeroShots, ScreenTour } from "../components/Screens";
import { RegionBadges } from "../components/Region";
import { BackupNotice, StatusBoard } from "../components/Status";
import { useReveal, useStats } from "../hooks";

const MARQUEE = ["Fotos in den Ordner", "PDFs finden", "Notizen", "Outlook", "Kalender", "Absprachen", "Web-App fürs Handy"];

const STATEMENT =
  "HandVero bringt *Fotos, *PDFs, *Termine und *Absprachen in eine *Ordnerstruktur. Gebaut für Betriebe, die lieber *arbeiten als *suchen. Am PC im Büro und mit dem Handy auf der Baustelle.";

type Tab = "as" | "el" | "ab";

export function Home() {
  const stats = useStats();
  const left = stats?.founderSlotsLeft ?? FOUNDER_SLOTS;
  const [tab, setTab] = useState<Tab>("as");
  useReveal();

  return (
    <section data-page="start" className="fun">
      <LineArtDefs />
      <div className="hero2">
        <div className="wrap hero2-grid">
          <div className="hero2-copy">
            <span className="kicker">▼ Software für Handwerksbetriebe</span>
            <h1 className="mega">
              <span className="ln1">
                <span>Foto.</span>
              </span>{" "}
              <span className="ln1">
                <span>Ordner.</span>
              </span>{" "}
              <span className="ln1">
                <span className="outline">Fertig.</span>
              </span>
            </h1>
            <p className="hero2-lead">
              HandVero bringt Fotos, PDFs, Termine und Absprachen in eine Ordnerstruktur. Am PC im Büro und mit dem Handy auf der Baustelle.
            </p>
            <div className="hero-actions">
              <Link className="btn btn-white" to="/registrieren">
                {left > 0 ? "Startplatz sichern" : "Kostenlos registrieren"}
              </Link>
              <a className="btn btn-line" href="#so-anker">
                So geht's
              </a>
            </div>
          </div>
          <div className="hero2-visual">
            {left > 0 && (
              <div className="sticker" aria-label={`Startaktion: 3 Jahre gratis für die ersten ${FOUNDER_SLOTS} Firmen, noch ${left} Plätze frei`}>
                <svg viewBox="0 0 200 200" aria-hidden="true">
                  <defs>
                    <path id="stk" d="M100 100m-78 0a78 78 0 1 1 156 0a78 78 0 1 1-156 0" />
                  </defs>
                  <g className="stk-ring">
                    <text>
                      <textPath href="#stk" startOffset="0" textLength="486" lengthAdjust="spacing">
                        3 JAHRE GRATIS ✦ ERSTE 100 FIRMEN ✦ 3 JAHRE GRATIS ✦ ERSTE 100 FIRMEN ✦
                      </textPath>
                    </text>
                  </g>
                </svg>
                <div className="stk-core">
                  <b>{left}</b>
                  <span>Plätze frei</span>
                </div>
              </div>
            )}
            <HeroShots />
          </div>
        </div>
      </div>

      <div className="mq-wrap">
        <div className="marquee" aria-hidden="true">
          <div className="mq-track">
            {[...MARQUEE, ...MARQUEE].map((m, i) => (
              <Fragment key={i}>
                <span>{m}</span>
                <i>✦</i>
              </Fragment>
            ))}
          </div>
        </div>
      </div>

      <div className="wrap chapter">
        <div className="kick">
          <span>01</span>
          <span>▼ Was es ist</span>
        </div>
        <Statement text={STATEMENT} />
      </div>

      <div className="wrap chapter">
        <div className="kick">
          <span>02</span>
          <span>▼ Von hier</span>
        </div>
        <div className="region-hero" style={{ backgroundImage: "linear-gradient(90deg, rgba(14,26,48,.88) 0%, rgba(14,26,48,.55) 60%, rgba(14,26,48,.25) 100%), url(/fotos/erzgebirge.jpg)" }}>
          <div className="region-head">
            <h2>Aus dem Erzgebirge. Für Betriebe vor Ort.</h2>
            <p className="lead">Von hier aus wird HandVero gebaut: von Menschen, die den Alltag auf der Baustelle kennen.</p>
          </div>
          <div className="region-claims" aria-hidden="true">
            <span>Weniger Papier.</span>
            <span>Weniger Fahrten.</span>
            <span>Mehr Zeit für die Arbeit.</span>
          </div>
        </div>
        <RegionBadges />
      </div>

      <div className="wrap chapter">
        <div className="kick">
          <span>03</span>
          <span>▼ Kennen Sie das?</span>
        </div>
        <div className="notes">
          <div className="note n1">
            <span className="mono">Fotos: 10 Handys, 0 Ordner</span>
            <h3>Fotos auf zehn Handys</h3>
            <p>Die Bilder von der Baustelle liegen bei jedem Monteur. Wer den Bericht schreibt, sucht sie zusammen.</p>
          </div>
          <div className="note n2">
            <span className="mono">Anhang: Angebot_final_2.pdf</span>
            <h3>PDFs im Postfach</h3>
            <p>Angebote, Pläne und Protokolle stecken in Mails. Zum Auftrag gehören sie trotzdem.</p>
          </div>
          <div className="note n3">
            <span className="mono">Termin: „irgendwann nächste Woche“</span>
            <h3>Absprachen im Kopf</h3>
            <p>Was mit dem Kunden besprochen wurde, steht nirgends. Ab jetzt steht es im Projekt.</p>
          </div>
        </div>
      </div>

      <div className="wrap chapter" id="so-anker">
        <div className="kick">
          <span>04</span>
          <span>▼ So geht's</span>
        </div>
        <div className="player-row">
          <DemoPlayer />
          <div className="bigsteps">
            <div className="bigstep reveal">
              <b>1</b>
              <div>
                <h3>Firma registrieren</h3>
                <p>Formular ausfüllen, E-Mail bestätigen.</p>
              </div>
            </div>
            <div className="bigstep reveal">
              <b>2</b>
              <div>
                <h3>Web-App aufs Handy</h3>
                <p>Auf dem Handy anmelden und zum Startbildschirm hinzufügen.</p>
              </div>
            </div>
            <div className="bigstep reveal">
              <b>3</b>
              <div>
                <h3>Fotos in den Ordner</h3>
                <p>Ordner wählen, Foto machen. Es landet dort, wo Sie es später suchen.</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="wrap chapter">
        <div className="kick">
          <span>05</span>
          <span>▼ Was drin ist</span>
        </div>
        <ScreenTour />
        <div className="feat4">
          <Link className="feat reveal" to="/funktionen">
            <span className="mono">Ordner</span>
            <h3>Strukturen, die zu Ihrem Betrieb passen</h3>
            <p>Anlegen, umbauen, als Vorlage speichern.</p>
            <i>→</i>
          </Link>
          <Link className="feat reveal" to="/web-app">
            <span className="mono">Kamera</span>
            <h3>Foto schießen, schon abgelegt</h3>
            <p>Direkt vom Handy in den richtigen Ordner.</p>
            <i>→</i>
          </Link>
          <Link className="feat reveal" to="/funktionen">
            <span className="mono">Nachweise · bald verfügbar</span>
            <h3>Protokolle und Nachweise im Griff</h3>
            <p>Kommt mit der Windows-Software: Unterweisungen, Prüfungen und Abnahmen mit Erinnerung.</p>
            <i>→</i>
          </Link>
          <Link className="feat reveal" to="/funktionen">
            <span className="mono">Outlook</span>
            <h3>Termine und Absprachen am Projekt</h3>
            <p>Kalender und Absprachen gehören dazu.</p>
            <i>→</i>
          </Link>
        </div>
      </div>

      <div className="wrap chapter">
        <div className="kick">
          <span>06</span>
          <span>▼ Angebot bis Mahnung</span>
        </div>
        <div className="proof-head">
          <span className="tag">In den Paketen enthalten</span>
          <h2>Vom Angebot bis zur Mahnung. Im selben Projekt.</h2>
          <p className="lead">
            Kunden, Angebote, Aufträge und Rechnungen liegen dort, wo auch Fotos und Dokumente liegen. Zahlungen und Ausgaben haben ihren Beleg, offene Rechnungen
            erinnern Sie, bevor etwas liegen bleibt. Es ist in allen bezahlten Paketen enthalten. Für Firmen ohne Paket ist ein eigenes Zusatzabo geplant. Die Ansicht zeigt Beispieldaten.
          </p>
        </div>
        <div className="ulist">
          <div className="urow head">
            <span>Beleg</span>
            <span>Kunde und Projekt</span>
            <span>Betrag</span>
            <span>Status</span>
          </div>
          {[
            ["AN-1042", "Wohnbau Schneider, Zählerschrank", "3.450,00 €", "soon", "Angebot offen"],
            ["RE-2031", "Praxis Dr. Weber, Elektroinstallation", "1.890,00 €", "ok", "Bezahlt"],
            ["RE-2032", "Autohaus Lenz, Ladestation", "2.380,00 €", "soon", "Fällig in 6 Tagen"],
            ["RE-2029", "Familie Hoffmann, Neubau", "980,00 €", "late", "Zahlungserinnerung fällig"],
          ].map(([no, who, sum, st, label], i) => (
            <div className="urow" key={i}>
              <span>{no}</span>
              <span>{who}</span>
              <span>{sum}</span>
              <span className={`st ${st}`}>{label}</span>
            </div>
          ))}
        </div>
        <p className="note-small">Beispieldaten, so wird es aussehen. HandVero ersetzt keine Steuerberatung.</p>
      </div>

      <div className="wrap chapter">
        <div className="kick">
          <span>07</span>
          <span>▼ Alle Nachweise</span>
        </div>
        <div className="proof-head">
          <span className="tag">Bald verfügbar</span>
          <h2>Jeder Nachweis an seinem Platz.</h2>
          <p className="lead">
            Prüfprotokolle, Unterweisungen, Gefährdungsbeurteilungen und Abnahmen. HandVero legt sie zum Projekt oder zum Mitarbeiter und erinnert
            Sie, bevor etwas fällig wird. Diese Funktionen kommen mit der Windows-Software und sind noch nicht nutzbar. Die Ansichten hier zeigen,
            wie es aussehen wird.
          </p>
        </div>
        <div className="tabs" role="tablist" aria-label="Nachweise">
          {(
            [
              ["as", "Arbeitsschutz"],
              ["el", "Elektroprüfung"],
              ["ab", "Abnahme und Wartung"],
            ] as [Tab, string][]
          ).map(([id, label]) => (
            <button key={id} role="tab" id={`tab-${id}`} aria-selected={tab === id} aria-controls={`pn-${id}`} onClick={() => setTab(id)}>
              {label}
            </button>
          ))}
        </div>
        <div className="panel" role="tabpanel" id="pn-as" aria-labelledby="tab-as" hidden={tab !== "as"}>
          <div className="as-grid">
            <div className="ulist">
              <div className="urow head">
                <span>Mitarbeiter</span>
                <span>Unterweisung</span>
                <span>Nächste fällig</span>
                <span>Status</span>
              </div>
              {[
                ["Jan Müller", "Jährliche Unterweisung Arbeitsschutz", "09/2027", "ok", "Erledigt"],
                ["Lena Vogt", "Leitern und Tritte", "31.10.2026", "soon", "In 14 Tagen"],
                ["Tom Krause", "Arbeiten unter Spannung", "02.10.2026", "late", "Überfällig"],
                ["Alle", "Gefährdungsbeurteilung Baustelle Schneider", "vor Beginn", "ok", "Erledigt"],
                ["Jan Müller", "Erste Hilfe im Betrieb", "03/2027", "ok", "Erledigt"],
              ].map(([who, what, due, st, label], i) => (
                <div className="urow" key={i}>
                  <span>{who}</span>
                  <span>{what}</span>
                  <span>{due}</span>
                  <span className={`st ${st}`}>{label}</span>
                </div>
              ))}
            </div>
            <ProofPaper />
          </div>
          <p className="note-small">Beispieldaten, so wird es aussehen. Erinnerungen kommen später per E-Mail und erscheinen im Kalender.</p>
        </div>
        <div className="panel" role="tabpanel" id="pn-el" aria-labelledby="tab-el" hidden={tab !== "el"}>
          <BoardDemo />
        </div>
        <div className="panel" role="tabpanel" id="pn-ab" aria-labelledby="tab-ab" hidden={tab !== "ab"}>
          <div className="ulist">
            <div className="urow head">
              <span>Projekt</span>
              <span>Protokoll</span>
              <span>Datum</span>
              <span>Status</span>
            </div>
            {[
              ["Wohnbau Schneider", "Abnahme Zählerschrank, 14 Fotos", "15.10.2026", "ok", "Vom Kunden unterschrieben"],
              ["Praxis Dr. Weber", "Übergabe Elektroinstallation", "08.10.2026", "ok", "Abgeschlossen"],
              ["Autohaus Lenz", "Wartung Ladestation", "12.11.2026", "soon", "Geplant"],
              ["Familie Hoffmann", "Mängelliste Neubau, 6 Punkte", "20.10.2026", "late", "2 offen"],
            ].map(([who, what, due, st, label], i) => (
              <div className="urow" key={i}>
                <span>{who}</span>
                <span>{what}</span>
                <span>{due}</span>
                <span className={`st ${st}`}>{label}</span>
              </div>
            ))}
          </div>
          <p className="note-small">Beispieldaten, so wird es aussehen. Protokolle unterschreibt der Kunde später direkt auf dem Handy oder Tablet.</p>
        </div>
      </div>

      <div className="wrap chapter" id="stand">
        <div className="kick">
          <span>08</span>
          <span>▼ Stand heute</span>
        </div>
        <div className="proof-head">
          <span className="tag">Demo</span>
          <h2>Was heute schon geht.</h2>
          <p className="lead">
            HandVero ist in der Startphase. Website, Handy-Web-App und Windows-Software können Sie jetzt nutzen. Protokolle, Schulungen und weitere Zusatzmodule kommen später.
          </p>
        </div>
        <StatusBoard />
        <BackupNotice />
      </div>

      <div className="wrap chapter">
        <div className="cta-mega">
          <div className="kick light">
            <span>09</span>
            <span>▼ Jetzt starten</span>
          </div>
          <div className="cta-mega-grid">
            {left > 0 ? (
              <>
                <div className="count">
                  <CountUp value={left} />
                  <span>freie Startplätze</span>
                </div>
                <div className="stack">
                  <h2>3 Jahre kostenlos. Für die ersten {FOUNDER_SLOTS} Firmen.</h2>
                  <p>Mit bis zu 3 Mitarbeitern, der Web-App und der Windows-Software. Danach gratis mit 1 Mitarbeiter oder im Abo.</p>
                  <div className="hero-actions">
                    <Link className="btn btn-white" to="/registrieren">
                      Startplatz sichern
                    </Link>
                    <Link className="btn btn-line" to="/preise">
                      Preise ansehen
                    </Link>
                  </div>
                </div>
              </>
            ) : (
              <div className="stack">
                <h2>Kostenlos starten.</h2>
                <p>Mit einem Zugang ohne Laufzeitende gratis. Für mehr Mitarbeiter gibt es Abos ab 9 € netto im Monat.</p>
                <div className="hero-actions">
                  <Link className="btn btn-white" to="/registrieren">
                    Kostenlos registrieren
                  </Link>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
